import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CostCentersService } from '../../../services/cost-centers.service';
import { CostCenter } from '../../../models/CostCenter';
import { FinancialMirrorService } from '../../../services/financial-mirror.service';
import { CashflowService } from '../../../services/cashflow.service';
import { FinancialMirrorDateMode, FinancialMirrorEntry } from '../../../models/FinancialMirror';
import { ErrorService } from '../../../shared/services/error.service';
import { downloadTableAsExcel } from '../../../utils/excel-export.util';
import { exportMirrorTableToPdf } from '../../../utils/financial-report-export.util';

@Component({
  selector: 'app-cashflow',
  templateUrl: './cashflow.component.html',
  styleUrls: ['./cashflow.component.css']
})
export class CashflowComponent implements OnInit, OnDestroy {
  tableTitle = 'Espelho financeiro';
  showSearch = true;
  searchPlaceholder = 'Pesquisar documento, descrição, contrato...';
  clickable = true;

  columns = [
    { titulo: 'Data', label: 'date', pipe: 'date' },
    { titulo: 'Tipo', label: 'type' },
    { titulo: 'Origem', label: 'sourceLabel' },
    { titulo: 'Documento', label: 'documentNumber' },
    { titulo: 'Descrição', label: 'description' },
    { titulo: 'Status', label: 'status' },
    { titulo: 'Valor', label: 'amount', pipe: 'currency' }
  ];

  actions = [
    { label: 'Abrir origem', icon: 'fas fa-arrow-up-right-from-square', action: 'view', class: 'btn-view' }
  ];

  /** Linhas exibidas na tabela (com campos auxiliares para busca). */
  entries: Array<FinancialMirrorEntry & { sourceLabel: string; searchText: string }> = [];
  loading = false;
  error: string | null = null;

  startDate = '';
  endDate = '';
  selectedCostCenterId: number | null = null;
  costCenters: CostCenter[] = [];
  dateMode: FinancialMirrorDateMode = 'due';
  typeFilter: 'all' | 'in' | 'out' = 'all';

  totalIn = 0;
  totalOut = 0;
  balance = 0;
  countIn = 0;
  countOut = 0;
  isExporting = false;
  isExportingExcel = false;
  isExportingPdf = false;

  private allEntries: FinancialMirrorEntry[] = [];
  private searchTerm = '';
  private destroy$ = new Subject<void>();

  constructor(
    private financialMirrorService: FinancialMirrorService,
    private cashflowService: CashflowService,
    private costCentersService: CostCentersService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.loadCostCenters();
    this.initDefaultPeriodAndLoad();
    this.cashflowService.onRefreshRequested
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyFilters());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCostCenters(): void {
    this.costCentersService.getActive().subscribe({
      next: (result) => { this.costCenters = result.items || []; },
      error: () => { this.costCenters = []; }
    });
  }

  private initDefaultPeriodAndLoad(): void {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    this.startDate = firstDay.toISOString().split('T')[0];
    this.endDate = lastDay.toISOString().split('T')[0];
    this.applyFilters();
  }

  onSearch(term: string): void {
    this.searchTerm = (term || '').trim().toLowerCase();
    this.applyLocalFilters();
  }

  onDateRangeChange(): void {
    this.applyFilters();
  }

  onCostCenterFilterChange(): void {
    this.applyFilters();
  }

  onDateModeChange(): void {
    this.applyFilters();
  }

  onTypeFilterChange(): void {
    this.applyLocalFilters();
  }

  applyFilters(): void {
    if (!this.startDate || !this.endDate) return;
    this.loading = true;
    this.error = null;

    this.financialMirrorService
      .loadMirror(this.startDate, this.endDate, this.dateMode, this.selectedCostCenterId)
      .subscribe({
        next: ({ entries, totals }) => {
          this.allEntries = entries;
          this.totalIn = totals.totalIn;
          this.totalOut = totals.totalOut;
          this.balance = totals.balance;
          this.countIn = totals.countIn;
          this.countOut = totals.countOut;
          this.loading = false;
          this.applyLocalFilters();
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.error = err?.message || 'Erro ao carregar espelho financeiro';
          this.loading = false;
          this.allEntries = [];
          this.entries = [];
          this.cdr.markForCheck();
        }
      });
  }

  private applyLocalFilters(): void {
    let list = [...this.allEntries];
    if (this.typeFilter === 'in') {
      list = list.filter((e) => e.type === 'Entrada');
    } else if (this.typeFilter === 'out') {
      list = list.filter((e) => e.type === 'Saída');
    }
    if (this.searchTerm) {
      list = list.filter((e) => {
        const text = [
          e.documentNumber,
          e.description,
          e.status,
          e.contractOrOrderRef,
          e.counterpartyName,
          e.type,
          e.source === 'accounts-receivable' ? 'receber' : 'pagar'
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return text.includes(this.searchTerm);
      });
    }
    this.entries = list.map((e) => ({
      ...e,
      sourceLabel: e.source === 'accounts-receivable' ? 'A receber' : 'A pagar',
      searchText: ''
    }));
  }

  clearFilters(): void {
    this.selectedCostCenterId = null;
    this.typeFilter = 'all';
    this.searchTerm = '';
    this.dateMode = 'due';
    this.initDefaultPeriodAndLoad();
  }

  onActionClick(actionData: { action: string; item: FinancialMirrorEntry }): void {
    if (actionData.action !== 'view' || !actionData.item) return;
    this.openSource(actionData.item);
  }

  onRowClick(item: FinancialMirrorEntry): void {
    if (item) this.openSource(item);
  }

  private openSource(item: FinancialMirrorEntry): void {
    if (item.source === 'accounts-receivable') {
      this.router.navigate(['/financial/accounts-receivable', item.sourceId, 'view']);
    } else {
      this.router.navigate(['/financial/accounts-payable', item.sourceId, 'view']);
    }
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);
  }

  get dateModeLabel(): string {
    return this.dateMode === 'due' ? 'Vencimento' : 'Recebimento / Pagamento';
  }

  private buildExportRows(): { headers: string[]; rows: (string | number)[][]; metaLines: string[] } {
    const headers = [
      'Data',
      'Tipo',
      'Origem',
      'Documento',
      'Descrição',
      'Status',
      'Valor (R$)',
      'Centro de custo',
      'Cliente / Fornecedor',
      'Contrato / Pedido',
      'Liquidado'
    ];

    const rows = this.entries.map((e) => [
      this.formatDateBr(e.date),
      e.type,
      e.sourceLabel,
      e.documentNumber,
      e.description,
      e.status,
      Number(e.amount) || 0,
      e.costCenterName || '—',
      e.counterpartyName || '—',
      e.contractOrOrderRef || '—',
      e.isSettled ? 'Sim' : 'Não'
    ]);

    const filteredIn = this.entries
      .filter((e) => e.type === 'Entrada')
      .reduce((s, e) => s + e.amount, 0);
    const filteredOut = this.entries
      .filter((e) => e.type === 'Saída')
      .reduce((s, e) => s + e.amount, 0);

    const metaLines = [
      `Período: ${this.formatDateBr(this.startDate)} a ${this.formatDateBr(this.endDate)}`,
      `Critério de data: ${this.dateModeLabel}`,
      `Centro de custo: ${this.getCostCenterLabel()}`,
      `Fluxo: ${this.getTypeFilterLabel()}`,
      ...(this.searchTerm ? [`Busca: ${this.searchTerm}`] : []),
      `Registros: ${this.entries.length} | Entradas: ${this.formatCurrency(filteredIn)} | Saídas: ${this.formatCurrency(filteredOut)} | Saldo: ${this.formatCurrency(filteredIn - filteredOut)}`
    ];

    return { headers, rows, metaLines };
  }

  /** Exporta para Excel os mesmos registros exibidos na tabela (filtros + busca aplicados). */
  exportToExcel(): void {
    if (this.loading) return;
    if (!this.entries.length) {
      this.errorService.showErrorMessage('Não há registros na tabela para exportar. Ajuste os filtros ou o período.');
      return;
    }

    this.isExporting = true;
    this.isExportingExcel = true;
    try {
      const { headers, rows, metaLines } = this.buildExportRows();
      const metaRows: string[][] = [
        ['Espelho financeiro — Flit Core'],
        ...metaLines.map((l) => [l])
      ];

      const stamp = new Date().toISOString().split('T')[0];
      downloadTableAsExcel(`espelho_financeiro_${stamp}`, headers, rows, metaRows, {
        sheetName: 'Espelho financeiro',
        numericColumnIndexes: [6]
      });
      this.errorService.showInfoMessage(
        `Planilha Excel exportada com ${this.entries.length} registro(s).`
      );
    } catch {
      this.errorService.showErrorMessage('Erro ao gerar planilha Excel.');
    } finally {
      this.isExporting = false;
      this.isExportingExcel = false;
      this.cdr.markForCheck();
    }
  }

  /** Exporta para PDF os mesmos registros visíveis na tabela. */
  exportToPdf(): void {
    if (this.loading) return;
    if (!this.entries.length) {
      this.errorService.showErrorMessage('Não há registros na tabela para exportar. Ajuste os filtros ou o período.');
      return;
    }

    this.isExporting = true;
    this.isExportingPdf = true;
    try {
      const { headers, rows, metaLines } = this.buildExportRows();
      exportMirrorTableToPdf(
        'Espelho financeiro — Flit Core',
        metaLines,
        headers,
        rows,
        'espelho_financeiro'
      );
      this.errorService.showInfoMessage(`PDF exportado com ${this.entries.length} registro(s).`);
    } catch {
      this.errorService.showErrorMessage('Erro ao gerar PDF.');
    } finally {
      this.isExporting = false;
      this.isExportingPdf = false;
      this.cdr.markForCheck();
    }
  }

  private formatDateBr(iso: string): string {
    if (!iso) return '—';
    const [y, m, d] = iso.split('T')[0].split('-');
    if (!y || !m || !d) return iso;
    return `${d}/${m}/${y}`;
  }

  private getCostCenterLabel(): string {
    if (this.selectedCostCenterId == null) return 'Todos';
    const cc = this.costCenters.find((c) => c.id === this.selectedCostCenterId);
    return cc?.name || String(this.selectedCostCenterId);
  }

  private getTypeFilterLabel(): string {
    if (this.typeFilter === 'in') return 'Somente entradas';
    if (this.typeFilter === 'out') return 'Somente saídas';
    return 'Entradas e saídas';
  }
}
